import React, {useMemo} from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import {Box, Typography} from "@mui/material";

const TOOLTIP_STYLE = "padding:8px 12px;background:#fff;border:1px solid #ECECF3;border-radius:10px;" +
  "box-shadow:0 6px 20px rgba(35,38,77,0.10);white-space:nowrap;font-family:Inter,sans-serif;";

// Top of the y-axis: a bit of headroom over the highest value, rounded up to the shown decimals.
// Kept an even number of steps so the middle label (top / 2) is exact too.
const resolveTop = (maxValue, decimals) => {
  const factor = 10 ** decimals;
  let steps = Math.ceil(maxValue * 1.15 * factor) || 1;
  if (steps % 2 === 1) steps += 1;
  return steps / factor;
};

const axisLabelSx = {
  position: "absolute",
  left: 12,
  fontSize: 10,
  lineHeight: 1,
  color: "#C4C4D2",
  pointerEvents: "none",
};

// Small area chart with the y-axis labels (top, middle, 0) drawn inside its left edge.
// points: [{x: date-time, y: number}], oldest first. decimals: rounding of the y-axis labels.
// formatX / formatY render the tooltip; an all-zero series is drawn as a flat line just above the bottom.
// showAxis: false hides the y-axis labels (the x-axis is never shown).
export const Sparkline = ({
                            points = [], color = "#8491F7", height = 64, decimals = 1, formatX, formatY, name = "Value",
                            showAxis = true
                          }) => {
  const series = useMemo(() => [{
    name,
    data: points.map(point => ({x: moment(point.x).valueOf(), y: Number(point.y) || 0})),
  }], [points, name]);

  const maxValue = Math.max(0, ...points.map(point => Number(point.y) || 0));
  const top = resolveTop(maxValue, decimals);

  const options = {
    chart: {
      type: "area",
      sparkline: {enabled: true},
      // Apex gives its wrapper a min-height of height + 15px by default, overflowing the fixed-height box
      parentHeightOffset: 0,
      toolbar: {show: false},
      zoom: {enabled: false},
      animations: {enabled: false},
    },
    colors: [color],
    stroke: {curve: "smooth", width: 2.25},
    fill: {
      type: "gradient",
      gradient: {shadeIntensity: 0, opacityFrom: 0.32, opacityTo: 0.02, stops: [0, 100]},
    },
    xaxis: {
      type: "datetime",
      labels: {show: false},
      axisBorder: {show: false},
      axisTicks: {show: false},
    },
    // Fixed scale so the overlaid labels line up with the plot; Apex's own labels are hidden (they are drawn
    // outside the plot and unrounded)
    yaxis: {
      min: maxValue > 0 ? 0 : -1,
      max: top,
      labels: {show: false},
      axisBorder: {show: false},
      axisTicks: {show: false},
    },
    grid: {show: false, padding: {left: 0, right: 0}},
    markers: {size: 0, hover: {size: 4}},
    dataLabels: {enabled: false},
    tooltip: {
      intersect: false,
      followCursor: true,
      custom: ({dataPointIndex}) => {
        const point = points[dataPointIndex];
        if (!point) return "";
        const label = formatX ? formatX(point.x) : moment(point.x).format("DD MMM HH:mm");
        const value = formatY ? formatY(point.y) : point.y;
        return `<div style="${TOOLTIP_STYLE}">
          <div style="font-size:11px;color:#8A8AA0;margin-bottom:2px;">${label}</div>
          <div style="font-size:13px;font-weight:600;color:#23264D;">${value}</div>
        </div>`;
      },
    },
  };

  return (
    <Box sx={{
      position: "relative",
      height,
      width: "100%",
      "& .apexcharts-canvas, & .apexcharts-svg": {overflow: "visible !important"},
      "& .apexcharts-tooltip": {pointerEvents: "none", border: "0 !important", boxShadow: "none !important", bgcolor: "transparent !important"},
    }}>
      <Chart options={options} series={series} type={"area"} height={height} width={"100%"}/>
      {showAxis && <>
        <Typography sx={{...axisLabelSx, top: 2}}>{top.toFixed(decimals)}</Typography>
        <Typography sx={{...axisLabelSx, top: "50%", transform: "translateY(-50%)"}}>
          {(top / 2).toFixed(decimals)}
        </Typography>
        <Typography sx={{...axisLabelSx, bottom: 2}}>{(0).toFixed(decimals)}</Typography>
      </>}
    </Box>
  );
};
